import { GameConfig } from "../config/GameConfig";

export type LevelResult = { stars: number; completed: boolean };

export type GameState = {
    coins: number;
    energy: number;
    walletAmount: number;
    currentLevel: number;
    maxUnlocked: number;
    levels: Record<number, LevelResult>;
};

const DEFAULT_STATE: GameState = {
    coins: GameConfig.START_COINS,
    energy: GameConfig.START_ENERGY,
    walletAmount: 0,
    currentLevel: 1,
    maxUnlocked: 1,
    levels: {},
};

export class GameStore extends Laya.EventDispatcher {
    static readonly CHANGED = "store-changed";
    private static _instance: GameStore;
    private _state: GameState;

    static get instance(): GameStore {
        if (!this._instance) this._instance = new GameStore();
        return this._instance;
    }

    private constructor() {
        super();
        this._state = this.load();
    }

    get state(): GameState {
        return this._state;
    }

    selectLevel(level: number): void {
        this._state.currentLevel = Math.max(1, Math.min(level, this._state.maxUnlocked, GameConfig.TOTAL_LEVELS));
        this.commit();
    }

    canStartLevel(): boolean {
        return this._state.energy >= GameConfig.LEVEL_ENERGY_COST;
    }

    spendEnergy(): boolean {
        if (!this.canStartLevel()) return false;
        this._state.energy -= GameConfig.LEVEL_ENERGY_COST;
        this.commit();
        return true;
    }

    spendCoins(amount: number): boolean {
        if (this._state.coins < amount) return false;
        this._state.coins -= amount;
        this.commit();
        return true;
    }

    completeCurrentLevel(): void {
        const level = this._state.currentLevel;
        this._state.coins += GameConfig.CORRECT_REWARD;
        this._state.levels[level] = { stars: 3, completed: true };
        this._state.maxUnlocked = Math.min(GameConfig.TOTAL_LEVELS, Math.max(this._state.maxUnlocked, level + 1));
        this.commit();
    }

    goNextLevel(): number {
        const next = Math.min(GameConfig.TOTAL_LEVELS, this._state.maxUnlocked, this._state.currentLevel + 1);
        this._state.currentLevel = next;
        this.commit();
        return next;
    }

    skipCurrentLevel(): number {
        const level = this._state.currentLevel;
        this._state.maxUnlocked = Math.min(GameConfig.TOTAL_LEVELS, Math.max(this._state.maxUnlocked, level + 1));
        this._state.currentLevel = Math.min(GameConfig.TOTAL_LEVELS, this._state.maxUnlocked, level + 1);
        this.commit();
        return this._state.currentLevel;
    }

    refillEnergy(): void {
        this._state.energy = GameConfig.MAX_ENERGY;
        this.commit();
    }

    reset(): void {
        this._state = JSON.parse(JSON.stringify(DEFAULT_STATE));
        this.commit();
    }

    private load(): GameState {
        try {
            const raw = Laya.LocalStorage.getItem(GameConfig.STORAGE_KEY);
            if (!raw) return JSON.parse(JSON.stringify(DEFAULT_STATE));
            const data = JSON.parse(raw) as Partial<GameState>;
            return {
                ...JSON.parse(JSON.stringify(DEFAULT_STATE)),
                ...data,
                // 红包余额只以广告分销后台为准，历史演示金额不再沿用。
                walletAmount: 0,
                currentLevel: Math.max(1, Math.min(data.currentLevel || 1, GameConfig.TOTAL_LEVELS)),
                maxUnlocked: Math.max(1, Math.min(data.maxUnlocked || 1, GameConfig.TOTAL_LEVELS)),
                levels: data.levels || {},
            };
        } catch (_error) {
            return JSON.parse(JSON.stringify(DEFAULT_STATE));
        }
    }

    private commit(): void {
        Laya.LocalStorage.setItem(GameConfig.STORAGE_KEY, JSON.stringify(this._state));
        this.event(GameStore.CHANGED, this._state);
    }
}
